import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FlaskConical, Plus, ToggleLeft, UserCog } from "lucide-react";
import {
  createExperiment,
  fetchExperiments,
  fetchExperimentAssignments,
  fetchFlags,
  setExperiment,
  setFlag,
} from "@/lib/queries";
import { ago, date, number } from "@/lib/format";
import type { Experiment } from "@/lib/types";

export function FlagsPage() {
  const queryClient = useQueryClient();
  const flags = useQuery({ queryKey: ["flags"], queryFn: fetchFlags });
  const experiments = useQuery({ queryKey: ["experiments"], queryFn: fetchExperiments });
  const [selectedExperimentKey, setSelectedExperimentKey] = useState<string | null>(null);

  const experimentAssignments = useQuery({
    queryKey: ["experiment-assignments", selectedExperimentKey],
    queryFn: () => selectedExperimentKey ? fetchExperimentAssignments(selectedExperimentKey) : Promise.resolve([]),
    enabled: !!selectedExperimentKey,
  });

  // Click experiment row to view its assignments
  function selectExperiment(key: string) {
    setSelectedExperimentKey(key === selectedExperimentKey ? null : key);
  }
  const [key, setKey] = useState("");
  const [name, setName] = useState("");
  const [variants, setVariants] = useState("control, treatment");
  const [traffic, setTraffic] = useState(100);
  const [notice, setNotice] = useState<string | null>(null);

  const flagMutation = useMutation({
    mutationFn: (input: { key: string; patch: Parameters<typeof setFlag>[1] }) =>
      setFlag(input.key, input.patch),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["flags"] }),
    onError: (error: Error) => setNotice(error.message),
  });

  const experimentMutation = useMutation({
    mutationFn: (input: { key: string; patch: Parameters<typeof setExperiment>[1] }) =>
      setExperiment(input.key, input.patch),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["experiments"] }),
    onError: (error: Error) => setNotice(error.message),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      createExperiment({
        key: key.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_"),
        name: name.trim(),
        variants: variants.split(",").map((value) => value.trim()).filter(Boolean),
        traffic_percent: traffic,
      }),
    onSuccess: () => {
      setKey("");
      setName("");
      setVariants("control, treatment");
      setTraffic(100);
      setNotice("Experiment created as a draft.");
      void queryClient.invalidateQueries({ queryKey: ["experiments"] });
    },
    onError: (error: Error) => setNotice(error.message),
  });

  return (
    <>
      <div className="page-head">
        <div>
          <h2 style={{ fontSize: 15 }}>Ship changes without an app update</h2>
          <p>Flags are read by the app at launch. Rollouts are percentage buckets by user id.</p>
        </div>
        <span className="badge badge-grape">
          <ToggleLeft size={12} />
          {number(flags.data?.length ?? 0)} flags
        </span>
      </div>

      {notice && <div className="banner banner-info">{notice}</div>}

      <section className="card">
        <div className="card-head">
          <div>
            <h2>Feature flags</h2>
            <p>Changes apply on the next app launch</p>
          </div>
        </div>
        <div style={{ marginTop: 14 }}>
          {flags.isLoading && <div className="loading-block"><div className="spinner" /></div>}
          {(flags.data ?? []).map((flag) => (
            <div className="flag-row" key={flag.key}>
              <button
                className={`switch ${flag.enabled ? "on" : ""}`}
                aria-label={`Toggle ${flag.key}`}
                onClick={() =>
                  flagMutation.mutate({ key: flag.key, patch: { enabled: !flag.enabled } })
                }
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="flag-key">{flag.key}</div>
                <div className="tiny faint">{flag.description ?? "No description"}</div>
              </div>
              <div className="row" style={{ gap: 6 }}>
                <span className="tiny faint">rollout</span>
                <input
                  key={flag.updated_at}
                  className="input"
                  type="number"
                  min={0}
                  max={100}
                  defaultValue={flag.rollout_percent}
                  style={{ width: 68, padding: "5px 7px", fontSize: 12 }}
                  onBlur={(event) => {
                    const value = Math.max(0, Math.min(100, Number(event.target.value)));
                    if (value !== flag.rollout_percent) {
                      flagMutation.mutate({
                        key: flag.key,
                        patch: { rollout_percent: value },
                      });
                    }
                  }}
                />
                <span className="tiny faint">%</span>
              </div>
              <span className={`badge ${flag.enabled ? "badge-mint" : ""}`}>
                {flag.enabled ? "on" : "off"}
              </span>
            </div>
          ))}
        </div>
      </section>

      <div className="grid" style={{ gridTemplateColumns: "minmax(0, 1.5fr) minmax(0, 1fr)" }}>
        <section className="card">
          <div className="card-head">
            <div>
              <h2>Experiments</h2>
              <p>Assignment is sticky per user and stored server-side</p>
            </div>
          </div>
          <div className="table-wrap" style={{ marginTop: 14 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Key</th>
                  <th>Name</th>
                  <th>Variants</th>
                  <th>Traffic</th>
                  <th>Status</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                {experiments.isLoading && (
                  <tr><td colSpan={6} className="empty"><div className="spinner" /></td></tr>
                )}
                {(experiments.data ?? []).map((experiment) => (
                  <tr 
                    key={experiment.key}
                    className={selectedExperimentKey === experiment.key ? "clickable selected-row" : "clickable"}
                    style={{ cursor: "pointer" }}
                    onClick={() => selectExperiment(experiment.key)}
                  >
                    <td className="mono cell-strong">{experiment.key}</td>
                    <td>{experiment.name}</td>
                    <td className="muted">{experiment.variants.join(" / ")}</td>
                    <td>
                      <input
                        className="input"
                        type="number"
                        min={0}
                        max={100}
                        defaultValue={experiment.traffic_percent}
                        style={{ width: 64, padding: "5px 7px", fontSize: 12 }}
                        onBlur={(event) => {
                          const value = Math.max(0, Math.min(100, Number(event.target.value)));
                          if (value !== experiment.traffic_percent) {
                            experimentMutation.mutate({
                              key: experiment.key,
                              patch: { traffic_percent: value },
                            });
                          }
                        }}
                      />
                    </td>
                    <td>
                      <select
                        className="select"
                        value={experiment.status}
                        style={{ width: 110, padding: "5px 7px", fontSize: 12 }}
                        onChange={(event) =>
                          experimentMutation.mutate({
                            key: experiment.key,
                            patch: { status: event.target.value as Experiment["status"] },
                          })
                        }
                      >
                        {["draft", "running", "paused", "completed"].map((status) => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </select>
                    </td>
                    <td className="nowrap faint">{date(experiment.starts_at)}</td>
                  </tr>
                ))}
                {(experiments.data ?? []).length === 0 && !experiments.isLoading && (
                  <tr><td colSpan={6} className="empty">No experiments yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="card">
          <div className="card-head">
            <div>
              <h2>Experiment assignments</h2>
              <p>User variant assignments tracked for compliance</p>
            </div>
            <div className="spacer" />
            <span className="badge badge-sunshine">
              <UserCog size={12} style={{ marginRight: 4 }} />
              assignment audit
            </span>
          </div>
          <div className="table-wrap" style={{ marginTop: 14 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Experiment</th>
                  <th>User ID</th>
                  <th>Variant</th>
                  <th>Assigned at</th>
                </tr>
              </thead>
              <tbody>
                {(experimentAssignments.isLoading && (
                  <tr>
                    <td colSpan={4} className="empty"><div className="spinner" /></td>
                  </tr>
                ))}
                {!experimentAssignments.isLoading && selectedExperimentKey && (experimentAssignments.data ?? []).length === 0 && (
                  <tr><td colSpan={4} className="empty">No assignments recorded yet.</td></tr>
                )}
                {!experimentAssignments.isLoading && (experimentAssignments.data ?? []).map((assignment) => (
                  <tr key={`${assignment.user_id}-${assignment.experiment_key}`}>
                    <td className="cell-strong">{assignment.experiment_key}</td>
                    <td className="mono faint">{assignment.user_id.slice(0, 8)}</td>
                    <td><span className="badge badge-mint">{assignment.variant}</span></td>
                    <td className="nowrap faint">{ago(assignment.assigned_at)}</td>
                  </tr>
                ))}
                {!selectedExperimentKey && (
                  <tr><td colSpan={4} className="empty">Select an experiment above to see assignments.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="card">
          <div className="card-head">
            <div>
              <h2>New experiment</h2>
              <p>Created as a draft; flip to running when ready.</p>
            </div>
          </div>
          <div className="card-pad stack">
            <div className="field">
              <label htmlFor="exp-key">Key</label>
              <input
                id="exp-key"
                className="input"
                placeholder="paywall_copy_v2"
                value={key}
                onChange={(event) => setKey(event.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="exp-name">Name</label>
              <input
                id="exp-name"
                className="input"
                placeholder="Annual-first paywall copy"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="exp-variants">Variants (comma separated)</label>
              <input
                id="exp-variants"
                className="input"
                value={variants}
                onChange={(event) => setVariants(event.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="exp-traffic">Traffic {traffic}%</label>
              <input
                id="exp-traffic"
                type="range"
                min={0}
                max={100}
                value={traffic}
                onChange={(event) => setTraffic(Number(event.target.value))}
              />
            </div>
            <button
              className="btn btn-primary"
              disabled={!key.trim() || !name.trim() || createMutation.isPending}
              onClick={() => createMutation.mutate()}
            >
              <Plus size={14} />
              Create draft
            </button>
            <p className="tiny faint">
              <FlaskConical size={11} style={{ verticalAlign: -2 }} /> Variants are assigned by
              hashing the user id. No one switches mid-experiment.
            </p>
          </div>
        </section>
      </div>
    </>
  );
}
